import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import { ArrowLeft, ArrowRight, ExternalLink, Filter, Newspaper, Calendar, Tag } from 'lucide-react';
import NewsletterBlock from '../components/NewsletterBlock';
import { NewsletterSchwebeButton } from '../components/NewsletterSchwebeButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reihenfolge & Anzeigenamen identisch zu SpecialAreas
const AREAS = [
  ['auslaenderrecht', 'Ausländerrecht'],
  ['strafrecht', 'Strafrecht'],
  ['steuerberatung', 'Steuerberatung'],
  ['steuerrecht', 'Steuerrecht'],
  ['gesellschaftsrecht', 'Gesellschaftsrecht'],
  ['verwaltungsrecht', 'Verwaltungsrecht'],
  ['mietrecht', 'Mietrecht'],
  ['verkehrsrecht', 'Verkehrsrecht'],
];

const formatDate = (iso, locale) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
};

const Aktuelles = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeArea = searchParams.get('rechtsgebiet') || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (activeArea) params.rechtsgebiet = activeArea;
      const res = await axios.get(`${API}/news`, { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [activeArea]);

  useEffect(() => {
    load();
  }, [load]);

  const setArea = (slug) => {
    if (slug) setSearchParams({ rechtsgebiet: slug });
    else setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title={t('aktuelles.seo_title', 'Aktuelle Urteile & Nachrichten – Kanzlei Nabhan Gäbler & Partner')}
        description={t('aktuelles.seo_desc', 'Täglich aktualisierte Urteile und Pressemitteilungen aus den Rechtsgebieten der Kanzlei – BGH, BFH, BVerwG. Kanzlei-tonal aufbereitet.')}
        path="/aktuelles"
      />
      <Header />
      <NewsletterSchwebeButton activeArea={activeArea} />

      {/* Hero */}
      <section className="bg-[#1B3A5C] text-white pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-1 text-white/70 text-sm hover:text-[#b8893d] mb-6 tracking-wide">
            <ArrowLeft className="w-3.5 h-3.5" /> {t('common.back_home', 'Zurück zur Startseite')}
          </Link>
          <div className="inline-flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4">
            <span className="w-12 h-px bg-[#b8893d]"></span>
            {t('aktuelles.eyebrow', 'AKTUELLES')}
          </div>
          <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5">
            {t('aktuelles.headline', 'Rechtsprechung im Überblick')}
          </h1>
          <p className="text-white/80 text-lg leading-relaxed max-w-3xl">
            {t('aktuelles.intro', 'Täglich aktualisierte Urteile, Beschlüsse und Pressemitteilungen aus BGH, BFH und BVerwG – kanzlei-tonal aufbereitet, sortiert nach unseren Rechtsgebieten.')}
          </p>
        </div>
      </section>

      <main className="bg-[#f5f8fa] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Filter */}
          <div className="mb-10">
            <div className="flex items-center gap-2 text-[#b8893d] text-xs tracking-[0.3em] mb-4">
              <Filter className="w-3.5 h-3.5" />
              {t('aktuelles.filter_label', 'NACH RECHTSGEBIET FILTERN')}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="filter-all"
                onClick={() => setArea('')}
                className={`px-4 py-2 text-xs tracking-wider border transition-colors ${
                  !activeArea
                    ? 'bg-[#1B3A5C] text-white border-[#1B3A5C]'
                    : 'bg-white text-[#1B3A5C] border-[#1B3A5C]/20 hover:border-[#1B3A5C]/50'
                }`}
              >
                {t('aktuelles.all', 'ALLE')} ({total >= 0 && !activeArea ? total : ''})
              </button>
              {AREAS.map(([slug, label]) => (
                <button
                  key={slug}
                  type="button"
                  data-testid={`filter-${slug}`}
                  onClick={() => setArea(slug)}
                  className={`px-4 py-2 text-xs tracking-wider border transition-colors ${
                    activeArea === slug
                      ? 'bg-[#b8893d] text-white border-[#b8893d]'
                      : 'bg-white text-[#1B3A5C] border-[#1B3A5C]/20 hover:border-[#b8893d]/50'
                  }`}
                >
                  {label.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Liste */}
          {loading && (
            <div className="text-center py-20 text-[#34536b]">{t('common.loading', 'Lade…')}</div>
          )}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-sm">
              {t('aktuelles.error', 'Aktuelle Nachrichten konnten nicht geladen werden.')} ({error})
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="bg-white border border-[#b8893d]/30 rounded-sm p-10 text-center">
              <Newspaper className="w-10 h-10 text-[#b8893d] mx-auto mb-3" />
              <p className="text-[#34536b]">{t('aktuelles.empty', 'Derzeit liegen keine Beiträge in diesem Rechtsgebiet vor.')}</p>
            </div>
          )}

          <div className="space-y-5">
            {items.map((n) => {
              const areaLabel = (AREAS.find(([s]) => s === n.rechtsgebiet) || ['', n.rechtsgebiet])[1];
              return (
                <article
                  key={n.id}
                  data-testid={`news-card-${n.id}`}
                  className="group bg-white border-l-4 border-[#b8893d] rounded-sm shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                  {n.image_url && (
                    <figure className="relative w-full bg-[#1B3A5C]/5">
                      <img
                        src={n.image_url}
                        alt={n.headline}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={(e) => { e.currentTarget.parentNode.style.display = 'none'; }}
                        className="block w-full aspect-[21/9] object-cover"
                        data-testid={`news-image-${n.id}`}
                      />
                      {n.image_credit && (
                        <figcaption className="absolute bottom-0 right-0 bg-black/55 text-white/90 text-[10px] italic px-2 py-0.5 tracking-wide">
                          {n.image_credit}
                        </figcaption>
                      )}
                    </figure>
                  )}
                  <div className="p-6 md:p-7">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] tracking-[0.15em] mb-3">
                      <span className="text-[#b8893d] font-semibold uppercase">{areaLabel}</span>
                      <span className="text-[#34536b]/60 flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {formatDate(n.published_at, i18n.language)}
                      </span>
                      <span className="text-[#34536b]/60">·</span>
                      <span className="text-[#34536b]/60 uppercase">{n.source}</span>
                    </div>
                    <h3 className="font-serif text-xl md:text-2xl text-[#1B3A5C] mb-3 leading-snug">
                      {n.headline}
                    </h3>
                    <p className="text-[#34536b] leading-relaxed text-[15px] mb-4">{n.summary}</p>
                    {Array.isArray(n.tags) && n.tags.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mb-4">
                        <Tag className="w-3.5 h-3.5 text-[#b8893d]" />
                        {n.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-[11px] px-2 py-0.5 bg-[#f5f8fa] text-[#34536b] tracking-wide"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {n.source_url && (
                      <a
                        href={n.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-[#b8893d] text-sm hover:gap-2 transition-all"
                      >
                        {t('aktuelles.source_link', 'Originalquelle')} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </article>
              );
            })}
          </div>

          {/* Newsletter-CTA */}
          <NewsletterBlock activeArea={activeArea} />

          {/* Disclaimer */}
          <div className="mt-12 p-5 bg-white/60 border-l-2 border-[#1B3A5C]/30 text-xs text-[#34536b]/80 leading-relaxed">
            {t('aktuelles.disclaimer', 'Hinweis: Die Inhalte werden automatisiert aus offiziellen Pressemitteilungen deutscher Bundesgerichte (BGH, BFH, BVerwG) zusammengestellt und redaktionell kanzlei-tonal aufbereitet. Sie ersetzen keine individuelle Rechtsberatung. Für eine konkrete Einschätzung Ihres Falls vereinbaren Sie bitte eine Erstberatung.')}
          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-[#1B3A5C] font-serif text-2xl mb-4">
              {t('aktuelles.cta_question', 'Betrifft eines dieser Urteile Ihren Fall?')}
            </p>
            <Link to="/mandantenerfassung">
              <Button className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-[0.1em] sm:tracking-[0.15em] text-xs px-6 sm:px-8 py-5 whitespace-normal h-auto max-w-full">
                {t('aktuelles.cta_button', 'JETZT ERSTBERATUNGSTERMIN ANFRAGEN')}
                <ArrowRight className="ml-2 w-3.5 h-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default Aktuelles;
